import { AbsenceType, PolicyRuleType } from '@abs/shared-types';
import { resolveActiveTier, resolveLetterGrade, computeTotalDeduction } from './penaltyResolver';
import { computeCreditHourDeduction } from './budgetCalculator';
/**
 * Credit-hour-weighted total deduction.
 * When creditHours differs from 3.0, deductions are scaled proportionally.
 */
function computeWeightedDeduction(totalDeduction, creditHours) {
    return computeCreditHourDeduction(totalDeduction, creditHours);
}
/**
 * Compute the full grade impact analysis.
 * Returns null if baseGrade is null (can't compute without a base grade).
 *
 * @param creditHours - Course credit hours for weighting (default 3.0)
 * @param attendanceWeight - If set, attendance is a weighted grade category (e.g., 0.10 for 10%)
 */
export function computeGradeImpact(baseGrade, gradingScale, policy, effectiveAbsences, entries, creditHours, attendanceWeight) {
    if (baseGrade === null)
        return null;
    const rawDeduction = computeTotalDeduction(policy, effectiveAbsences);
    const credits = creditHours ?? 3.0;
    const totalDeduction = computeWeightedDeduction(rawDeduction, credits);
    // If attendance is a weighted category, compute differently
    if (attendanceWeight !== undefined && attendanceWeight > 0) {
        return computeWeightedGradeImpact(baseGrade, gradingScale, policy, effectiveAbsences, entries, attendanceWeight, credits);
    }
    const currentGradePoints = Math.max(0, baseGrade - totalDeduction);
    const currentGrade = resolveLetterGrade(currentGradePoints, gradingScale);
    // Project next-absence impact
    const projectedAbsences = {
        ...effectiveAbsences,
        totalPenaltyAbsences: effectiveAbsences.totalPenaltyAbsences + 1,
    };
    const projectedRawDeduction = computeTotalDeduction(policy, projectedAbsences);
    const projectedDeduction = computeWeightedDeduction(projectedRawDeduction, credits);
    const nextAbsencePoints = Math.max(0, baseGrade - projectedDeduction);
    const nextAbsenceGrade = resolveLetterGrade(nextAbsencePoints, gradingScale);
    const penaltyPerAbsence = effectiveAbsences.totalPenaltyAbsences > 0
        ? totalDeduction / effectiveAbsences.totalPenaltyAbsences
        : 0;
    const auditTrail = buildAuditTrail(policy, effectiveAbsences, entries, baseGrade, totalDeduction, gradingScale, credits);
    return {
        currentGrade,
        currentGradePoints,
        penaltyApplied: totalDeduction,
        nextAbsenceGrade,
        nextAbsencePoints,
        penaltyPerAbsence,
        auditTrail,
    };
}
/**
 * Compute grade impact when attendance is a weighted grade category.
 * e.g., "attendance = 10% of grade" → attendance grade contributes 10% to overall.
 */
function computeWeightedGradeImpact(baseGrade, gradingScale, policy, effectiveAbsences, entries, attendanceWeight, creditHours) {
    // Attendance grade: percentage of sessions attended × 100
    // This is used as a grade component weighted by attendanceWeight
    const totalEntries = entries.length;
    const presentCount = entries.filter((e) => e.type === AbsenceType.Present).length;
    const attendanceGrade = totalEntries > 0 ? (presentCount / totalEntries) * 100 : 100;
    // Combined grade: baseGrade weighted by (1 - attendanceWeight) + attendanceGrade weighted by attendanceWeight
    const nonAttendanceWeight = 1 - attendanceWeight;
    const combinedGrade = (baseGrade * nonAttendanceWeight) + (attendanceGrade * attendanceWeight);
    const combinedGradeClamped = Math.max(0, Math.min(100, combinedGrade));
    const currentGrade = resolveLetterGrade(combinedGradeClamped, gradingScale);
    // Also compute penalty deduction for the audit trail
    const rawDeduction = computeTotalDeduction(policy, effectiveAbsences);
    const totalDeduction = computeWeightedDeduction(rawDeduction, creditHours);
    // Project next absence
    const projectedTotalEntries = totalEntries + 1;
    const projectedAttendanceGrade = totalEntries > 0
        ? (presentCount / projectedTotalEntries) * 100
        : 0;
    const projectedCombined = (baseGrade * nonAttendanceWeight) + (projectedAttendanceGrade * attendanceWeight);
    const nextAbsencePoints = Math.max(0, Math.min(100, projectedCombined));
    const nextAbsenceGrade = resolveLetterGrade(nextAbsencePoints, gradingScale);
    const penaltyPerAbsence = effectiveAbsences.totalPenaltyAbsences > 0
        ? totalDeduction / effectiveAbsences.totalPenaltyAbsences
        : 0;
    const auditTrail = buildAuditTrail(policy, effectiveAbsences, entries, baseGrade, totalDeduction, gradingScale, creditHours);
    return {
        currentGrade,
        currentGradePoints: combinedGradeClamped,
        penaltyApplied: totalDeduction,
        nextAbsenceGrade,
        nextAbsencePoints,
        penaltyPerAbsence,
        auditTrail,
    };
}
/**
 * Build a step-by-step audit trail explaining how the penalty was computed.
 */
export function buildAuditTrail(policy, effectiveAbsences, entries, baseGrade, totalDeduction, gradingScale, creditHours) {
    const steps = [];
    let stepNum = 1;
    // Step 1: Raw absence counts
    const unexcusedRaw = entries.filter((e) => e.type === AbsenceType.Unexcused).length;
    const excusedRaw = entries.filter((e) => e.type === AbsenceType.Excused).length;
    const lateRaw = entries.filter((e) => e.type === AbsenceType.Late).length;
    const earlyRaw = entries.filter((e) => e.type === AbsenceType.EarlyDeparture).length;
    steps.push({
        step: stepNum++,
        label: 'Raw Absence Counts',
        input: `total entries: ${entries.length}`,
        output: `unexcused: ${unexcusedRaw}, excused: ${excusedRaw}, late: ${lateRaw}, early departure: ${earlyRaw}`,
        formula: 'count by type',
    });
    // Step 2: Grace allowance
    steps.push({
        step: stepNum++,
        label: 'Apply Grace Allowance',
        input: `unexcused: ${unexcusedRaw}, grace: ${policy.grace.unexcusedGrace}`,
        output: `effective unexcused: ${effectiveAbsences.effectiveUnexcused}`,
        formula: `max(0, ${unexcusedRaw} − ${policy.grace.unexcusedGrace}) = ${effectiveAbsences.effectiveUnexcused}`,
    });
    // Step 3: Tardy conversion
    if (policy.tardyConversion.enabled) {
        const totalTardies = lateRaw + earlyRaw;
        steps.push({
            step: stepNum++,
            label: 'Tardy Conversion',
            input: `tardies: ${totalTardies}, threshold: ${policy.tardyConversion.tardiesPerAbsence}`,
            output: `conversions: ${effectiveAbsences.tardyConversions}, remaining tardies: ${effectiveAbsences.remainingTardies}`,
            formula: `floor(${totalTardies} / ${policy.tardyConversion.tardiesPerAbsence}) = ${effectiveAbsences.tardyConversions}`,
        });
    }
    // Step 4: Excused limit
    if (policy.excusedAbsenceLimit !== null) {
        steps.push({
            step: stepNum++,
            label: 'Excused Absence Limit',
            input: `effective excused: ${effectiveAbsences.countedExcused}, limit: ${policy.excusedAbsenceLimit}`,
            output: `counted excused: ${effectiveAbsences.countedExcused}`,
            formula: `min(${effectiveAbsences.countedExcused}, ${policy.excusedAbsenceLimit})`,
        });
    }
    // Step 5: Total penalty absences
    steps.push({
        step: stepNum++,
        label: 'Total Penalty Absences',
        input: `unexcused: ${effectiveAbsences.effectiveUnexcused}, excused: ${effectiveAbsences.countedExcused}, tardy conversions: ${effectiveAbsences.tardyConversions}`,
        output: `total: ${effectiveAbsences.totalPenaltyAbsences}`,
        formula: `${effectiveAbsences.effectiveUnexcused} + ${effectiveAbsences.countedExcused} + ${effectiveAbsences.tardyConversions} = ${effectiveAbsences.totalPenaltyAbsences}`,
    });
    // Step 6: Active tier resolution
    const { activeTier } = resolveActiveTier(policy.tiers, effectiveAbsences.totalPenaltyAbsences);
    steps.push({
        step: stepNum++,
        label: 'Penalty Tier Resolution',
        input: `penalty absences: ${effectiveAbsences.totalPenaltyAbsences}`,
        output: activeTier ? `active tier: ${activeTier.description}` : 'no active tier',
        formula: activeTier
            ? `${effectiveAbsences.totalPenaltyAbsences} >= ${activeTier.absenceThreshold} → ${activeTier.description}`
            : 'below all thresholds',
    });
    // Step 7: Grade deduction
    steps.push({
        step: stepNum++,
        label: 'Grade Deduction',
        input: `base grade: ${baseGrade}`,
        output: `deduction: ${totalDeduction}`,
        formula: policy.ruleType === PolicyRuleType.PerAbsenceDeduction
            ? `per-absence rate × penalty absences = ${totalDeduction}`
            : `sum of applicable tier deductions = ${totalDeduction}`,
    });
    // Step 7b: Credit-hour weighting (if applicable)
    const credits = creditHours ?? 3.0;
    if (credits !== 3.0) {
        steps.push({
            step: stepNum++,
            label: 'Credit-Hour Weighting',
            input: `raw deduction: ${totalDeduction}, credit hours: ${credits}`,
            output: `weighted deduction: ${totalDeduction}`,
            formula: `deduction × (${credits} / 3.0) = ${totalDeduction}`,
        });
    }
    // Step 8: Resulting grade
    const finalPoints = Math.max(0, baseGrade - totalDeduction);
    const finalLetter = resolveLetterGrade(finalPoints, gradingScale);
    steps.push({
        step: stepNum++,
        label: 'Final Grade',
        input: `base: ${baseGrade}, deduction: ${totalDeduction}`,
        output: `${finalLetter} (${finalPoints.toFixed(1)}%)`,
        formula: `max(0, ${baseGrade} − ${totalDeduction}) = ${finalPoints.toFixed(1)}`,
    });
    return steps;
}
//# sourceMappingURL=gradeImpact.js.map